import type React from 'react';
import { useRef } from 'react';

import { Image } from '@change/ds-core-ui';

import type { AiGeneratedPetitionPhoto } from '#src/app/pages/petitionDetails/shared/types';

import { WatermarkedImage } from './components/WatermarkedImage';
import { MOBILE_MEDIA_QUERY, useAiHeroPhoto } from './hooks/useAiHeroPhoto';

/**
 * Hero photo for AI-generated petition images — a single element for all viewports (unlike the
 * legacy Photo/PhotoMobile pair). The watermark experiment's treatment arm bakes the mark in via
 * `WatermarkedImage`; control arms render the untouched image with browser-native resolution
 * switching. Bucketing is intent-to-treat: the hook treats on the render decision, and a failed
 * composite is reported (not excluded) — see useAiHeroPhoto.
 */
export const AiHeroPhoto: React.FC<Props> = ({ photo }) => {
	// carries the visibility signal for the hook's treat gate — see the treat effect in useAiHeroPhoto
	const rootRef = useRef<HTMLDivElement>(null);
	const {
		data: { watermarked, placement },
		actions: { onCompositeError },
	} = useAiHeroPhoto(rootRef);

	return (
		<div ref={rootRef}>
			{watermarked ? (
				// composited from the desktop-resolution source for every viewport: the mobile variant
				// is too small to survive the bake visually once upscaled on high-density screens.
				// rootRef doubles as the width measurement — the wrapper div and the image share a width
				<WatermarkedImage
					id="petition-photo"
					src={photo.url || photo.mobileUrl}
					placement={placement}
					measureRef={rootRef}
					onCompositeError={onCompositeError}
				/>
			) : (
				<picture>
					<source srcSet={photo.mobileUrl} media={MOBILE_MEDIA_QUERY} />
					<Image
						aspectRatio="16:9"
						id="petition-photo"
						src={photo.url || photo.mobileUrl}
						alt=""
						className="w-full align-top"
					/>
				</picture>
			)}
		</div>
	);
};

type Props = Readonly<{
	photo: AiGeneratedPetitionPhoto;
}>;
